import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import SEOHead from './SEOHead';
import { permeabilityUnits, popularPermeabilityConversions, convertPermeability, getUnitById } from '../data/permeabilityRoutes';
import { ArrowRightLeft } from 'lucide-react';

const PermeabilityConversionPage = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLocalizedPath();
  const { conversion } = useParams();
  const [inputValue, setInputValue] = useState('1');

  const [fromId, toId] = conversion?.split('-to-') || [];
  const fromUnit = getUnitById(fromId);
  const toUnit = getUnitById(toId);

  const convertedValue = useMemo(() => {
    const value = parseFloat(inputValue);
    if (isNaN(value) || !fromUnit || !toUnit) return '';
    const result = convertPermeability(value, fromId, toId);
    return result !== null ? result.toExponential(6) : '';
  }, [inputValue, fromId, toId, fromUnit, toUnit]);

  if (!fromUnit || !toUnit) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-red-600">{t('common.invalidConversion')}</h1>
        <p className="mt-4">{t('common.conversionNotAvailable')}</p>
        <LocalizedLink to="/convert/permeability" className="text-blue-600 hover:underline mt-4 inline-block">
          ← {t('common.backToConverter', { category: t('sidebar.converters.permeability') })}
        </LocalizedLink>
      </div>
    );
  }

  const conversionFactor = fromUnit.factor / toUnit.factor;
  const referenceValues = [0.001, 0.01, 0.1, 1, 5, 10, 50, 100, 500, 1000];

  const relatedConversions = popularPermeabilityConversions.filter(
    c => (c.from === fromId || c.to === fromId || c.from === toId || c.to === toId) &&
         !(c.from === fromId && c.to === toId)
  ).slice(0, 6);

  return (
    <>
      <SEOHead
        title={t('seo.convertTitle', { from: t(`permeabilityUnits.${fromUnit.id}.name`, fromUnit.name), to: t(`permeabilityUnits.${toUnit.id}.name`, toUnit.name) })}
        description={`Convert ${fromUnit.name} (${fromUnit.symbol}) to ${toUnit.name} (${toUnit.symbol}). Free online permeability converter with formula and conversion table.`}
        keywords={`${fromUnit.name} to ${toUnit.name}, ${fromUnit.symbol} to ${toUnit.symbol}, permeability conversion`}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="mb-4">
          <LocalizedLink to="/convert/permeability" className="text-blue-600 hover:underline">
            {t('common.backToConverter', { category: t('sidebar.converters.permeability') })}
          </LocalizedLink>
        </nav>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {t('common.convertXToY', { from: t(`permeabilityUnits.${fromUnit.id}.name`, fromUnit.name), to: t(`permeabilityUnits.${toUnit.id}.name`, toUnit.name) })}
        </h1>
        <p className="text-gray-600 mb-8">
          {t('common.convertXToYDescription', { from: t(`permeabilityUnits.${fromUnit.id}.name`, fromUnit.name), fromSymbol: fromUnit.symbol, to: t(`permeabilityUnits.${toUnit.id}.name`, toUnit.name), toSymbol: toUnit.symbol })}
        </p>

        {/* Converter Tool */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t(`permeabilityUnits.${fromUnit.id}.name`, fromUnit.name)} ({fromUnit.symbol})
              </label>
              <input
                type="number"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('common.enterValue')}
              />
            </div>

            <div className="self-center p-2">
              <ArrowRightLeft className="w-5 h-5 text-gray-400" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t(`permeabilityUnits.${toUnit.id}.name`, toUnit.name)} ({toUnit.symbol})
              </label>
              <div className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg font-medium">
                {convertedValue || '—'}
              </div>
            </div>
          </div>
        </div>

        {/* Conversion Formula */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('common.formula')}</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-mono text-lg">
              1 {fromUnit.symbol} = {conversionFactor.toExponential(6)} {toUnit.symbol}
            </p>
            <p className="mt-2 text-gray-600">
              {t('common.formulaDescriptionMultiply', { from: t(`permeabilityUnits.${fromUnit.id}.name`, fromUnit.name), to: t(`permeabilityUnits.${toUnit.id}.name`, toUnit.name), factor: conversionFactor.toExponential(6) })}
            </p>
          </div>
        </div>

        {/* Reference Table */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('common.conversionTable')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b">
                  <th className="py-2 px-4">{t(`permeabilityUnits.${fromUnit.id}.name`, fromUnit.name)} ({fromUnit.symbol})</th>
                  <th className="py-2 px-4">{t(`permeabilityUnits.${toUnit.id}.name`, toUnit.name)} ({toUnit.symbol})</th>
                </tr>
              </thead>
              <tbody>
                {referenceValues.map(val => {
                  const result = convertPermeability(val, fromId, toId);
                  return (
                    <tr key={val} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-4">{val}</td>
                      <td className="py-2 px-4">{result?.toExponential(4)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {/* About This Conversion Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('common.unitDescriptionTitle')}</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">{t('common.fromUnitLabel', { unit: t(`permeabilityUnits.${fromUnit.id}.name`, fromUnit.name) })}</h3>
              <p className="text-sm text-gray-600">{t(`permeabilityUnits.${fromUnit.id}.description`, t('common.noDescription'))}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">{t('common.toUnitLabel', { unit: t(`permeabilityUnits.${toUnit.id}.name`, toUnit.name) })}</h3>
              <p className="text-sm text-gray-600">{t(`permeabilityUnits.${toUnit.id}.description`, t('common.noDescription'))}</p>
            </div>
            <p className="text-sm text-gray-500 italic">{t('common.conversionNote', { from: t(`permeabilityUnits.${fromUnit.id}.plural`, fromUnit.plural), to: t(`permeabilityUnits.${toUnit.id}.plural`, toUnit.plural) })}</p>
          </div>
        </div>



        {/* Related Conversions */}
        {relatedConversions.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('common.relatedConversions')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {relatedConversions.map(({ from, to }) => {
                const fromU = permeabilityUnits.find(u => u.id === from);
                const toU = permeabilityUnits.find(u => u.id === to);
                if (!fromU || !toU) return null;
                return (
                  <LocalizedLink
                    key={`${from}-${to}`}
                    to={`/convert/permeability/${from}-to-${to}`}
                    className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <span>{t(`permeabilityUnits.${fromU.id}.name`, fromU.name)}</span>
                    <ArrowRightLeft className="w-4 h-4 text-gray-400" />
                    <span>{t(`permeabilityUnits.${toU.id}.name`, toU.name)}</span>
                  </LocalizedLink>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PermeabilityConversionPage;
